import { useState, useEffect } from "react";
import { supabase } from "@/lib/supabase";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { Trash2, Plus, ChevronUp, ChevronDown, AlertTriangle } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

// Types
interface CheckIn {
  check_in_id: string;
  name: string;
  report_config?: ReportConfig | null;
  active_question_ids?: string[];
}

interface ReportConfig {
  intro?: {
    title: string;
    content: string;
  };
  aiSummaries: AISummaryConfig[];
  customReportComponents: CustomReportComponent[];
  includeIndividualUpdatesComponent: boolean;
  version?: string;
}

interface AISummaryConfig {
  summaryId: string;
  displayName: string;
  summaryType: string;
  targetSectionKey: string;
  userInstructions?: string | null;
  sourceQuestionIds: string[];
}

interface CustomReportComponent {
  isEnabled: boolean;
  componentKey: string;
  displayOrder: number;
  titleOverride?: string | null;
  config?: Record<string, any> | null;
}

interface SummaryType {
  key: string;
  display_name: string;
  base_prompt: string;
  is_global: boolean;
}

interface ReportConfigEditorProps {
  checkIn: CheckIn;
  isOpen: boolean;
  onClose: () => void;
  onConfigUpdated: (updatedConfig: ReportConfig) => void;
}

// Required questions mapping
const SUMMARY_TYPE_REQUIRED_QUESTIONS = {
  'predefined_yesterday': '33333333-3333-3333-3333-333333333301',
  'predefined_today': '33333333-3333-3333-3333-333333333302',
  'predefined_blockers': '33333333-3333-3333-3333-333333333303'
} as const;

// Generate unique ID
const generateId = () => crypto.randomUUID();

// Generate target section key from summary type
const generateTargetSectionKey = (summaryType: string): string => {
  switch (summaryType) {
    case 'predefined_yesterday': return 'yesterday';
    case 'predefined_today': return 'today';
    case 'predefined_blockers': return 'blockers_overview';
    case 'predefined_accomplishments': return 'accomplishments';
    case 'predefined_challenges': return 'challenges';
    case 'predefined_morale': return 'morale';
    case 'predefined_risks': return 'risks';
    case 'custom': return 'custom_summary';
    default: return summaryType.replace('predefined_', '');
  }
};

// Helper to generate a unique custom section key
const getNextCustomSectionKey = (aiSummaries: AISummaryConfig[]): string => {
  const customKeys = aiSummaries
    .filter(s => s.summaryType === 'custom')
    .map(s => s.targetSectionKey)
    .filter(Boolean);
  let idx = 1;
  while (customKeys.includes(`custom_summary_${idx}`)) {
    idx++;
  }
  return `custom_summary_${idx}`;
};

// Default report config
const getDefaultReportConfig = (): ReportConfig => ({
  intro: {
    title: "Introduction",
    content: "Thank the teammate for calling in today and providing an update."
  },
  aiSummaries: [],
  customReportComponents: [],
  includeIndividualUpdatesComponent: true,
  version: "2.0"
});

// Ensure required questions are added
const ensureRequiredQuestions = async (
  checkInId: string,
  organizationId: string,
  summaryConfigs: AISummaryConfig[]
): Promise<string[]> => {
  const requiredGlobalQuestionIds = new Set<string>();
  
  // Collect required global question IDs
  summaryConfigs.forEach(config => {
    const requiredId = SUMMARY_TYPE_REQUIRED_QUESTIONS[config.summaryType as keyof typeof SUMMARY_TYPE_REQUIRED_QUESTIONS];
    if (requiredId) {
      requiredGlobalQuestionIds.add(requiredId);
    }
  });

  if (requiredGlobalQuestionIds.size === 0) {
    return [];
  }

  // Get current active questions for this check-in
  const { data: checkIn } = await supabase
    .from('check_ins')
    .select('active_question_ids')
    .eq('check_in_id', checkInId)
    .single();

  const currentQuestionIds = checkIn?.active_question_ids || [];

  // Check which required questions already exist (by created_from_question_id)
  const { data: existingQuestions } = await supabase
    .from('questions')
    .select('question_id, created_from_question_id')
    .in('question_id', currentQuestionIds);

  const existingGlobalQuestionIds = new Set(
    existingQuestions?.map(q => q.created_from_question_id).filter(Boolean) || []
  );

  // Find missing required questions
  const missingGlobalQuestionIds = Array.from(requiredGlobalQuestionIds)
    .filter(id => !existingGlobalQuestionIds.has(id));

  if (missingGlobalQuestionIds.length === 0) {
    return [];
  }

  // Create custom questions for missing global questions
  const newQuestionIds: string[] = [];
  
  for (const globalQuestionId of missingGlobalQuestionIds) {
    const { data: globalQuestion } = await supabase
      .from('questions')
      .select('header, format, questions, response_schema, prompt_instructions')
      .eq('question_id', globalQuestionId)
      .single();

    if (globalQuestion) {
      const { data: newQuestion } = await supabase
        .from('questions')
        .insert({
          header: globalQuestion.header,
          format: globalQuestion.format,
          questions: globalQuestion.questions,
          response_schema: globalQuestion.response_schema,
          prompt_instructions: globalQuestion.prompt_instructions,
          organization_id: organizationId,
          is_global: false,
          created_from_question_id: globalQuestionId
        })
        .select('question_id')
        .single();

      if (newQuestion) {
        newQuestionIds.push(newQuestion.question_id);
      }
    }
  }

  // Update check-in's active_question_ids
  if (newQuestionIds.length > 0) {
    const updatedQuestionIds = [...currentQuestionIds, ...newQuestionIds];
    await supabase
      .from('check_ins')
      .update({ active_question_ids: updatedQuestionIds })
      .eq('check_in_id', checkInId);
  }

  return newQuestionIds;
};

// Update pending reports with new config
const updatePendingReportsConfig = async (checkInId: string): Promise<void> => {
  // Get all pending reports for this check-in
  const { data: pendingReports, error: reportsError } = await supabase
    .from('reports')
    .select('report_id')
    .eq('check_in_id', checkInId)
    .eq('status', 'pending');

  if (reportsError) {
    console.error('Error fetching pending reports:', reportsError);
    throw reportsError;
  }

  if (!pendingReports || pendingReports.length === 0) {
    console.log('No pending reports to update');
    return;
  }

  // Update each pending report's report_instance_config
  const updatePromises = pendingReports.map(async (report) => {
    try {
      // Get updated check-in data
      const { data: checkIn, error: checkInError } = await supabase
        .from('check_ins')
        .select('report_config, active_question_ids')
        .eq('check_in_id', checkInId)
        .single();

      if (checkInError) throw checkInError;

      const questionIds = checkIn?.active_question_ids || [];
      let questions: any[] = [];
      
      if (questionIds.length > 0) {
        const { data: questionsData, error: questionsError } = await supabase
          .from('questions')
          .select('question_id, header, prompt_instructions')
          .in('question_id', questionIds);
        
        if (questionsError) throw questionsError;
        questions = questionsData || [];
      }

      const reportInstanceConfig = {
        ...(checkIn?.report_config || getDefaultReportConfig()),
        activeQuestions: questions.map(q => ({
          questionId: q.question_id,
          header: q.header,
          promptInstructions: q.prompt_instructions
        }))
      };

      const { error: updateError } = await supabase
        .from('reports')
        .update({ report_instance_config: reportInstanceConfig })
        .eq('report_id', report.report_id);

      if (updateError) throw updateError;
      
      return { success: true, reportId: report.report_id };
    } catch (error) {
      console.error(`Failed to update pending report ${report.report_id}:`, error);
      return { success: false, reportId: report.report_id, error };
    }
  });

  const results = await Promise.allSettled(updatePromises);
  const successCount = results.filter(r => r.status === 'fulfilled').length;
  
  console.log(`Updated ${successCount} of ${pendingReports.length} pending reports`);
};

// Main update function
const updateCheckInReportConfig = async (
  checkInId: string,
  organizationId: string,
  reportConfig: ReportConfig
): Promise<{ success: boolean; addedQuestions: string[]; error?: string }> => {
  try {
    // 1. Ensure required questions are added
    const addedQuestions = await ensureRequiredQuestions(
      checkInId,
      organizationId,
      reportConfig.aiSummaries
    );

    // 2. Update check-in's report_config
    const { error: updateError } = await supabase
      .from('check_ins')
      .update({
        report_config: { ...reportConfig, version: '2.0' },
        updated_at: new Date().toISOString()
      })
      .eq('check_in_id', checkInId);

    if (updateError) throw updateError;

    // 3. Update all pending reports for this check-in
    await updatePendingReportsConfig(checkInId);

    return { success: true, addedQuestions };
  } catch (error) {
    console.error('Error updating report config:', error);
    return {
      success: false,
      addedQuestions: [],
      error: error instanceof Error ? error.message : String(error)
    };
  }
};

/**
 * Report Configuration Editor Component
 * 
 * Allows users to configure AI summaries, custom components, and introduction
 * for check-in reports. Automatically adds required questions when certain
 * summary types are selected and updates pending reports with new configuration.
 */
export function ReportConfigEditor({ checkIn, isOpen, onClose, onConfigUpdated }: ReportConfigEditorProps) {
  const [config, setConfig] = useState<ReportConfig>(
    checkIn.report_config || getDefaultReportConfig()
  );
  const [summaryTypes, setSummaryTypes] = useState<SummaryType[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingSummaryTypes, setIsLoadingSummaryTypes] = useState(false);
  const { user } = useAuth();

  // Load summary types
  useEffect(() => {
    const loadSummaryTypes = async () => {
      setIsLoadingSummaryTypes(true);
      try {
        const { data, error } = await supabase
          .from('summary_types')
          .select('key, display_name, base_prompt, is_global')
          .eq('is_global', true)
          .order('key');

        if (error) throw error;
        setSummaryTypes(data || []);
      } catch (error) {
        console.error('Error loading summary types:', error);
        toast.error('Failed to load summary types');
      } finally {
        setIsLoadingSummaryTypes(false);
      }
    };

    if (isOpen) {
      loadSummaryTypes();
    }
  }, [isOpen]);

  // Reset config when dialog opens
  useEffect(() => {
    if (isOpen) {
      setConfig(checkIn.report_config || getDefaultReportConfig());
    }
  }, [isOpen, checkIn.report_config]);

  const handleSave = async () => {
    if (!user) return;

    console.log('[ReportConfigEditor] Saving configuration:', config);
    
    // Basic validation
    if (config.aiSummaries.some(summary => !summary.displayName.trim())) {
      toast.error('All AI summaries must have a display name');
      return;
    }
    
    if (config.aiSummaries.some(summary => summary.summaryType === 'custom' && !summary.userInstructions?.trim())) {
      toast.error('Custom summaries must have instructions');
      return;
    }

    setIsSaving(true);
    try {
      // Get user's organization
      const { data: userData } = await supabase
        .from('users')
        .select('organization_id')
        .eq('user_id', user.id)
        .single();

      if (!userData?.organization_id) {
        throw new Error('Organization not found');
      }

      const result = await updateCheckInReportConfig(
        checkIn.check_in_id,
        userData.organization_id,
        config
      );

      if (!result.success) {
        throw new Error(result.error || 'Failed to save configuration');
      }

      if (result.addedQuestions.length > 0) {
        console.log('[ReportConfigEditor] Added questions:', result.addedQuestions);
        toast.success(`Configuration saved. Added ${result.addedQuestions.length} required questions.`);
      } else {
        toast.success('Configuration saved successfully');
      }

      onConfigUpdated(config);
      onClose();
    } catch (error) {
      console.error('Error saving report config:', error);
      toast.error('Failed to save configuration');
    } finally {
      setIsSaving(false);
    }
  };

  const addAISummary = (summaryType: string) => {
    const summaryTypeData = summaryTypes.find(st => st.key === summaryType);
    if (!summaryTypeData) return;

    let targetSectionKey: string;
    if (summaryType === 'custom') {
      targetSectionKey = getNextCustomSectionKey(config.aiSummaries);
    } else {
      targetSectionKey = generateTargetSectionKey(summaryType);
    }

    const newSummary: AISummaryConfig = {
      summaryId: generateId(),
      displayName: summaryTypeData.display_name + (summaryType === 'custom' ? ` (${targetSectionKey.replace('custom_summary_', '#')})` : ''),
      summaryType: summaryType,
      targetSectionKey,
      userInstructions: summaryType === 'custom' ? '' : null,
      sourceQuestionIds: checkIn.active_question_ids || []
    };

    setConfig(prev => ({
      ...prev,
      aiSummaries: [...prev.aiSummaries, newSummary]
    }));
  };

  const updateAISummary = (index: number, updates: Partial<AISummaryConfig>) => {
    setConfig(prev => ({
      ...prev,
      aiSummaries: prev.aiSummaries.map((summary, i) =>
        i === index ? { ...summary, ...updates } : summary
      )
    }));
  };

  const removeAISummary = (index: number) => {
    setConfig(prev => ({
      ...prev,
      aiSummaries: prev.aiSummaries.filter((_, i) => i !== index)
    }));
  };

  const moveAISummary = (index: number, direction: 'up' | 'down') => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= config.aiSummaries.length) return;

    setConfig(prev => {
      const newSummaries = [...prev.aiSummaries];
      [newSummaries[index], newSummaries[newIndex]] = [newSummaries[newIndex], newSummaries[index]];
      return { ...prev, aiSummaries: newSummaries };
    });
  };

  const updateBlockerComponent = (enabled: boolean, titleOverride?: string) => {
    setConfig(prev => {
      const otherComponents = prev.customReportComponents.filter(c => c.componentKey !== 'blocker_summary');
      
      if (enabled) {
        const component: CustomReportComponent = {
          isEnabled: true,
          componentKey: 'blocker_summary',
          displayOrder: 1,
          titleOverride: titleOverride || null
        };
        return {
          ...prev,
          customReportComponents: [...otherComponents, component]
        };
      } else {
        return {
          ...prev,
          customReportComponents: otherComponents
        };
      }
    });
  };

  const blockerComponent = config.customReportComponents.find(c => c.componentKey === 'blocker_summary');
  const availableSummaryTypes = summaryTypes.filter(st =>
    st.key !== 'custom' // allow unlimited custom
      ? !config.aiSummaries.some(summary => summary.summaryType === st.key)
      : true
  );

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Configure Report Summary</DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Warning if no questions */}
          {(!checkIn.active_question_ids || checkIn.active_question_ids.length === 0) && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <div className="flex items-center gap-2 text-amber-800">
                <AlertTriangle className="h-4 w-4" />
                <span className="font-medium">No Questions Configured</span>
              </div>
              <p className="text-sm text-amber-700 mt-1">
                This check-in has no questions yet. AI summaries will have no data to work with until you add questions to the check-in.
              </p>
            </div>
          )}

          {/* AI Summaries Section */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">AI Summaries</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {config.aiSummaries.map((summary, index) => (
                <Card key={summary.summaryId} className="border-l-4 border-l-primary">
                  <CardContent className="pt-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 space-y-3">
                        <div>
                          <Label>Display Name</Label>
                          <Input
                            value={summary.displayName}
                            onChange={(e) => updateAISummary(index, { displayName: e.target.value })}
                            placeholder="Summary display name"
                          />
                        </div>
                        
                        {summary.summaryType === 'custom' && (
                          <div>
                            <Label>Custom Instructions</Label>
                            <Textarea
                              value={summary.userInstructions || ''}
                              onChange={(e) => updateAISummary(index, { userInstructions: e.target.value })}
                              placeholder="Provide specific instructions for this custom summary..."
                              rows={2}
                            />
                          </div>
                        )}
                        
                        <div className="text-sm text-muted-foreground">
                          Type: {summaryTypes.find(st => st.key === summary.summaryType)?.display_name || summary.summaryType}
                        </div>
                      </div>
                      
                      <div className="flex flex-col gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => moveAISummary(index, 'up')}
                          disabled={index === 0}
                        >
                          <ChevronUp className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => moveAISummary(index, 'down')}
                          disabled={index === config.aiSummaries.length - 1}
                        >
                          <ChevronDown className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => removeAISummary(index)}
                          className="text-destructive hover:text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}

              {availableSummaryTypes.length > 0 && (
                <div className="flex items-center gap-2">
                  <Select onValueChange={addAISummary}>
                    <SelectTrigger className="w-64">
                      <SelectValue placeholder="Add AI Summary" />
                    </SelectTrigger>
                    <SelectContent>
                      {isLoadingSummaryTypes ? (
                        <SelectItem value="_loading" disabled>Loading...</SelectItem>
                      ) : (
                        availableSummaryTypes.map(summaryType => (
                          <SelectItem key={summaryType.key} value={summaryType.key}>
                            {summaryType.display_name}
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                  <Button variant="outline" size="icon" disabled>
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              )}

              {config.aiSummaries.length === 0 && (
                <div className="text-center py-8 text-muted-foreground">
                  No AI summaries configured. Add one to get started.
                </div>
              )}
            </CardContent>
          </Card>

          {/* Custom Components Section */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Report Components</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Blocker Summary Component */}
              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div className="space-y-1">
                  <div className="font-medium">Blocker Summary</div>
                  <div className="text-sm text-muted-foreground">
                    Shows a dedicated section for blockers and issues
                  </div>
                </div>
                <Switch
                  checked={blockerComponent?.isEnabled || false}
                  onCheckedChange={(checked) => updateBlockerComponent(checked, blockerComponent?.titleOverride || undefined)}
                />
              </div>

              {blockerComponent?.isEnabled && (
                <div className="ml-4 space-y-2">
                  <Label>Custom Title (optional)</Label>
                  <Input
                    value={blockerComponent.titleOverride || ''}
                    onChange={(e) => updateBlockerComponent(true, e.target.value)}
                    placeholder="Blockers"
                  />
                </div>
              )}

              <Separator />

              {/* Individual Updates Component */}
              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div className="space-y-1">
                  <div className="font-medium">Individual Updates</div>
                  <div className="text-sm text-muted-foreground">
                    Shows detailed responses from each team member
                  </div>
                </div>
                <Switch
                  checked={config.includeIndividualUpdatesComponent}
                  onCheckedChange={(checked) => setConfig(prev => ({
                    ...prev,
                    includeIndividualUpdatesComponent: checked
                  }))}
                />
              </div>
            </CardContent>
          </Card>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isSaving}>
            {isSaving ? 'Saving...' : 'Save Configuration'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
} 